import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AlertTriangle, XCircle, Filter, X, Search, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react'

interface Execution {
  id: number
  external_execution_id: string
  workflow_id: number
  workflow_name: string
  client_name: string
  instance_name: string
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  error_category: string | null
  error_category_label: string
  error_message: string | null
}

interface Client {
  id: string
  name: string
}

interface Instance {
  id: string
  name: string
  client_id: string
  client_name: string
}

interface Workflow {
  id: string
  name: string
  instance_id: string
  client_id: string
  client_name: string
}

interface Category {
  value: string
  label: string
}

interface ErrorSummary {
  total: number
  by_category: Array<{ category: string; label: string; count: number }>
  by_workflow: Array<{ name: string; count: number }>
}

interface Filters {
  client_id: string | null
  instance_id: string | null
  workflow_id: string | null
  category: string | null
}

interface Props {
  executions: Execution[]
  filters: Filters
  clients: Client[]
  instances: Instance[]
  workflows: Workflow[]
  categories: Category[]
  error_summary: ErrorSummary
}

type SortKey = 'workflow_name' | 'client_name' | 'started_at' | 'error_category_label' | 'error_message'
type SortDirection = 'asc' | 'desc'

function formatDate(dateString: string | null): string {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString()
}

export default function ErrorsIndex({
  executions,
  filters,
  clients,
  instances,
  workflows,
  categories,
  error_summary,
}: Props) {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>('started_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)

  const hasFilters = filters.client_id || filters.instance_id || filters.workflow_id || filters.category

  // Get instances filtered by selected client
  const filteredInstances = useMemo(() => {
    if (!filters.client_id) return instances
    return instances.filter(i => i.client_id === filters.client_id)
  }, [instances, filters.client_id])

  // Get workflows filtered by selected client and/or instance
  const filteredWorkflows = useMemo(() => {
    let filtered = workflows
    if (filters.client_id) {
      filtered = filtered.filter(w => w.client_id === filters.client_id)
    }
    if (filters.instance_id) {
      filtered = filtered.filter(w => w.instance_id === filters.instance_id)
    }
    return filtered
  }, [workflows, filters.client_id, filters.instance_id])

  // Filter executions locally by search
  const filteredExecutions = useMemo(() => {
    if (!search.trim()) return executions
    const searchLower = search.toLowerCase()
    return executions.filter(e =>
      e.workflow_name.toLowerCase().includes(searchLower) ||
      e.client_name.toLowerCase().includes(searchLower) ||
      e.instance_name.toLowerCase().includes(searchLower) ||
      e.external_execution_id.toLowerCase().includes(searchLower) ||
      (e.error_message && e.error_message.toLowerCase().includes(searchLower))
    )
  }, [executions, search])

  // Sort executions
  const sortedExecutions = useMemo(() => {
    if (!sortKey) return filteredExecutions
    return [...filteredExecutions].sort((a, b) => {
      let aVal: string | number | null
      let bVal: string | number | null

      if (sortKey === 'started_at') {
        aVal = a.started_at ? new Date(a.started_at).getTime() : null
        bVal = b.started_at ? new Date(b.started_at).getTime() : null
      } else if (sortKey === 'client_name') {
        aVal = `${a.client_name}/${a.instance_name}`
        bVal = `${b.client_name}/${b.instance_name}`
      } else {
        aVal = a[sortKey]
        bVal = b[sortKey]
      }

      if (aVal == null && bVal == null) return 0
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return sortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [filteredExecutions, sortKey, sortDirection])

  // Paginate executions
  const totalPages = Math.ceil(sortedExecutions.length / itemsPerPage)
  const paginatedExecutions = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage
    return sortedExecutions.slice(start, start + itemsPerPage)
  }, [sortedExecutions, currentPage, itemsPerPage])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection('asc')
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
  }

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const updateFilter = (key: keyof Filters, value: string | null) => {
    const newValue = value === 'all' ? null : value
    const newFilters = { ...filters, [key]: newValue }

    // Clear dependent filters when parent filter changes
    if (key === 'client_id') {
      newFilters.instance_id = null
      newFilters.workflow_id = null
    } else if (key === 'instance_id') {
      newFilters.workflow_id = null
    }

    router.get('/errors', newFilters, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const clearFilters = () => {
    router.get('/errors', {}, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <Layout>
      <PageHead title="Errors" description="View failed workflow executions and error details." />
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Errors</h1>
          <p className="text-muted-foreground">
            Failed workflow executions from the last 7 days.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Failures</CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{error_summary.total}</div>
            </CardContent>
          </Card>
          {error_summary.by_category.slice(0, 3).map((cat) => (
            <Card key={cat.category}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{cat.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{cat.count}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filters
                </CardTitle>
                <CardDescription>Filter errors by client, instance, workflow, or category.</CardDescription>
              </div>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="mr-2 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Select
                value={filters.client_id || 'all'}
                onValueChange={(value) => updateFilter('client_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.instance_id || 'all'}
                onValueChange={(value) => updateFilter('instance_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Instances" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Instances</SelectItem>
                  {filteredInstances.map((instance) => (
                    <SelectItem key={instance.id} value={instance.id}>
                      {filters.client_id ? instance.name : `${instance.name} (${instance.client_name})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.workflow_id || 'all'}
                onValueChange={(value) => updateFilter('workflow_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Workflows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workflows</SelectItem>
                  {filteredWorkflows.map((workflow) => (
                    <SelectItem key={workflow.id} value={workflow.id}>
                      {workflow.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={filters.category || 'all'}
                onValueChange={(value) => updateFilter('category', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.value} value={category.value}>
                      {category.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {executions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground" />
              <h2 className="mt-4 text-xl font-semibold">No errors found</h2>
              <p className="mt-2 text-center text-muted-foreground">
                {hasFilters ? 'Try adjusting your filters.' : 'No failed executions in the last 7 days.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Search and Page Size */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search errors..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              {executions.length > 10 && (
                <Select
                  value={String(itemsPerPage)}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleSort('workflow_name')} className="flex items-center font-medium hover:text-foreground">
                        Workflow <SortIcon column="workflow_name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('client_name')} className="flex items-center font-medium hover:text-foreground">
                        Client / Instance <SortIcon column="client_name" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('started_at')} className="flex items-center font-medium hover:text-foreground">
                        Time <SortIcon column="started_at" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => handleSort('error_category_label')} className="flex items-center font-medium hover:text-foreground">
                        Category <SortIcon column="error_category_label" />
                      </button>
                    </TableHead>
                    <TableHead>Error</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedExecutions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                        No errors match your search
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedExecutions.map((execution) => (
                      <TableRow key={execution.id}>
                        <TableCell>
                          <Link
                            href={`/errors/${execution.id}`}
                            className="font-medium hover:underline"
                          >
                            {execution.workflow_name}
                          </Link>
                          <p className="text-xs text-muted-foreground font-mono">
                            {execution.external_execution_id}
                          </p>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {execution.client_name} / {execution.instance_name}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(execution.started_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{execution.error_category_label}</Badge>
                        </TableCell>
                        <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                          {execution.error_message || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedExecutions.length)} of {sortedExecutions.length} errors
                  {search && ` (filtered from ${executions.length})`}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
